import { useSearchParams } from "react-router-dom";

import { type NeedLine, type OrderKind, type ResolvedScan, type ScannedExtra, parseKind } from "@/api/order_check";
import { ZERO_GUID } from "@/lib/format";
import { fmtQty } from "@/lib/scan-ui/status";

/** Вид проверки из адреса экрана.
 *
 * ⚠️ Читают его ТРИ экрана (сессия, её тело, паллета), и каждый по-своему
 * читать не должен: экраны создают сессию по ссылке заказа, и потерянный вид
 * уводит их искать перемещение среди заказов покупателя — то есть в 404 с
 * вечным спиннером. */
export function useOrderKind(): OrderKind {
  const [search] = useSearchParams();
  return parseKind(search.get("kind"));
}

/** Порог сравнения количеств (плавающая точка от коэффициентов упаковок). */
export const REMAINING_EPS = 0.001;

const nrm = (x?: string | null) => (x && x !== ZERO_GUID ? x : "");
/** Коэффициент единицы как ключ (устойчиво к float-шуму; коэф — целые). */
export const ck = (c?: number | null): number => Math.round((c || 1) * 1000) / 1000;

type NeedKeyBits = Pick<NeedLine, "nomenclature_ref" | "characteristic_ref" | "partia_ref" | "unit_coef">;

/**
 * Индекс потребностей заказа — зеркало раскладки бэка (`scanned_by_need`):
 * какие партии и какие единицы (коэффициенты) заказ различает для (ном, хар).
 */
export interface NeedIndex {
  partias: Record<string, Set<string>>; // "ном|хар" -> партии заказа
  coefs: Record<string, Set<number>>; // "ном|хар|партия" -> коэффициенты заказа
}

export function buildNeedIndex(needs: NeedKeyBits[]): NeedIndex {
  const idx: NeedIndex = { partias: {}, coefs: {} };
  for (const n of needs) {
    const nc = `${n.nomenclature_ref}|${nrm(n.characteristic_ref)}`;
    const p = nrm(n.partia_ref);
    if (p) (idx.partias[nc] ??= new Set()).add(p);
    (idx.coefs[`${nc}|${p}`] ??= new Set()).add(ck(n.unit_coef));
  }
  return idx;
}

/** Эффективная партия скана под заказ (партийный фолбэк, как в бэке). */
export function effectivePartia(nom: string, char: string, partia: string, idx: NeedIndex): string {
  const ps = idx.partias[`${nom}|${nrm(char)}`];
  if (!ps) return "";
  if (!nrm(partia) && ps.size === 1) return [...ps][0] ?? "";
  return nrm(partia);
}

/**
 * Эффективный коэффициент скана: заказ базовый (коэф=1) для этой (ном,хар,партия)
 * ИЛИ позиции нет → любую ед сворачиваем в базу (1); заказ различает единицы →
 * коэф скана как есть (совпал с коробовой строкой = она; нет = «не та единица»).
 */
export function effectiveCoef(
  nom: string,
  char: string,
  partia: string,
  scanCoef: number,
  idx: NeedIndex,
): number {
  const cs = idx.coefs[`${nom}|${nrm(char)}|${partia}`];
  if (!cs || (cs.size === 1 && cs.has(ck(1)))) return ck(1);
  return ck(scanCoef);
}

/** Ключ гранулярности скана: `ном|хар|партия|коэф` (партия+коэф разрешены под заказ). */
export function granKey(
  nom: string,
  char: string,
  partia: string,
  scanCoef: number,
  idx: NeedIndex,
): string {
  const p = effectivePartia(nom, char, partia, idx);
  const c = effectiveCoef(nom, char, p, scanCoef, idx);
  return `${nom}|${nrm(char)}|${p}|${c}`;
}

/** Ключ гранулярности строки потребности (для сопоставления с granKey скана). */
export function needGranKey(n: NeedKeyBits): string {
  return `${n.nomenclature_ref}|${nrm(n.characteristic_ref)}|${nrm(n.partia_ref)}|${ck(n.unit_coef)}`;
}

/** Нормализация ref (zero-guid/пусто → ""). Публичный алиас `nrm` для страниц. */
export const norm = nrm;

/**
 * Строка потребности под сканируемую (ном, хар, ПАРТИЯ, ЕДИНИЦА). Заказ различает
 * партии/единицы для этой (ном, хар) — берём точную строку; иначе — единственную.
 * Строит индекс внутри — в горячих местах (рендер шторки) мемоизировать результат.
 */
export function findNeed(needs: NeedLine[], nom: string, char: string, partia: string, coef: number): NeedLine | null {
  const idx = buildNeedIndex(needs);
  const key = granKey(nom, char, partia, coef, idx);
  return needs.find((n) => needGranKey(n) === key) ?? null;
}

/**
 * ПОЧЕМУ отсканированное не легло ни в одну потребность заказа.
 *
 * ⚠️ Проверка «не та единица» в разделе была с самого начала и отбивает скан
 * громко, а вот ЧУЖАЯ ПАРТИЯ и ЧУЖАЯ ХАРАКТЕРИСТИКА проходили молча: экран
 * смотрел, есть ли в заказе НОМЕНКЛАТУРА, и если есть — падал в авто-учёт с
 * зелёной карточкой. Кладовщик узнавал о расхождении только на «Завершить»,
 * когда паллета уже сложена: бэк отказывается создавать расходную и называет
 * позицию «не из заказа».
 *
 * Случай не выдуманный: один и тот же EAN13 стоит на флаконе для маркетплейса
 * и на нём же для опта, а заказ различает и партию, и характеристику — по ним
 * считаются срок годности и FIFO.
 *
 * В ДИРЕКТИВНОМ режиме такой скан и раньше отбивался — там гейт плана
 * (`wrong_partia`); эта функция даёт то же самое свободному режиму.
 */
export type OrderMismatch =
  | { kind: "partia"; expected: string[] }
  | { kind: "characteristic"; expected: string[] }
  | { kind: "unknown" };

export function explainNotInOrder(
  needs: NeedLine[],
  nom: string,
  char: string,
): OrderMismatch {
  const rows = needs.filter((n) => n.nomenclature_ref === nom);
  if (rows.length === 0) return { kind: "unknown" };
  const c = nrm(char);
  const sameChar = rows.filter((n) => nrm(n.characteristic_ref) === c);
  if (sameChar.length > 0) {
    // Товар и характеристика сошлись — значит расходится партия.
    const expected = [...new Set(sameChar.map((n) => n.partia_name || "без партии"))];
    return { kind: "partia", expected };
  }
  const expected = [...new Set(rows.map((n) => n.characteristic_name || "без характеристики"))];
  return { kind: "characteristic", expected };
}

/**
 * Текст отказа «Не та характеристика» для товара `nom`.
 *
 * ⚠️ Прод 02.10.2026, заказ НФНФ-000640: у строки заказа — СОБСТВЕННАЯ
 * характеристика товара «ОПТ», помеченная на удаление, а штрихкод короба и весь
 * остаток — под действующей общей «ОПТ». Отказ верный, но текст «идёт с
 * характеристикой «ОПТ». Отсканирована «ОПТ»» читался как сбой портала. Когда
 * названия совпадают при разных ссылках, говорим прямо: это РАЗНЫЕ
 * характеристики, чинить — строку заказа в 1С.
 */
export function characteristicMismatchText(
  needs: NeedLine[],
  nom: string,
  scanned: { ref: string; name: string },
  where = "В заказе",
): string {
  const c = nrm(scanned.ref);
  const byRef = new Map<string, NeedLine>();
  for (const n of needs) {
    if (n.nomenclature_ref === nom && nrm(n.characteristic_ref) !== c) {
      byRef.set(nrm(n.characteristic_ref), n);
    }
  }
  const expected = [...byRef.values()];
  const scannedName = scanned.name.trim();
  const twin = scannedName
    ? expected.find((n) => n.characteristic_name.trim() === scannedName)
    : undefined;
  if (twin) {
    return twin.characteristic_deleted
      ? `${where} — «${scannedName}» (помечена на удаление в 1С), отсканирована «${scannedName}» ` +
          "(действующая). Это разные характеристики с одинаковым названием — исправьте " +
          "характеристику строки заказа в 1С."
      : `${where} — «${scannedName}», отсканирована «${scannedName}». Это разные характеристики ` +
          "с одинаковым названием — проверьте характеристику строки заказа в 1С.";
  }
  // ⚠️ У маркетплейсовых заказов строка идёт БЕЗ характеристики (на все
  // площадки товар одинаков, различаем только опт), и это самый частый
  // случай. «Идёт с характеристикой «без характеристики»» читалось бы как
  // канцелярит — там нужна другая фраза.
  const names = [
    ...new Set(
      expected.map((n) =>
        n.characteristic_name
          ? `«${n.characteristic_name}»${n.characteristic_deleted ? " (помечена на удаление в 1С)" : ""}`
          : "без характеристики",
      ),
    ),
  ];
  const head =
    names.length === 1 && names[0] === "без характеристики"
      ? `${where} этот товар идёт БЕЗ характеристики. `
      : `${where} этот товар идёт с характеристикой ${names.join(" или ")}. `;
  return head + `Отсканирована ${scannedName ? `«${scannedName}»` : "позиция без характеристики"}.`;
}

/** Документ раздела — для текстов отказа «не та характеристика/партия». */
export type MismatchDoc = "order" | "transfer";

const DOC_WORDS: Record<MismatchDoc, { where: string; subject: string; of: string }> = {
  order: { where: "В заказе", subject: "заказ", of: "заказа" },
  transfer: { where: "В перемещении", subject: "перемещение", of: "перемещения" },
};

/**
 * Герой-отказ «отсканированное не легло ни в одну строку документа»: не та
 * характеристика, не та партия, иначе — не из документа. Один текст на сборку
 * и проверку перемещений: в перемещениях такой скан раньше молча становился
 * «лишним», и о расхождении узнавали только на «Завершить» (аудит 06.10, К8).
 */
export function notInOrderHero(
  needs: NeedLine[],
  nom: string,
  scanned: { ref: string; name: string },
  partiaGot: string,
  doc: MismatchDoc,
): { title: string; detail: string } {
  const w = DOC_WORDS[doc];
  const why = explainNotInOrder(needs, nom, scanned.ref);
  if (why.kind === "characteristic") {
    return { title: "Не та характеристика", detail: characteristicMismatchText(needs, nom, scanned, w.where) };
  }
  if (why.kind === "partia") {
    return {
      title: "Не та партия",
      detail:
        `По этому товару ${w.subject} ждёт партию ${why.expected.join(" или ")}. ` +
        `Отсканирована ${partiaGot ? `партия ${partiaGot}` : "позиция без партии"} — это другой товар для учёта.`,
    };
  }
  return {
    title: `Позиция не из ${w.of}`,
    detail: `Отсканированное не совпало ни с одной строкой ${w.of}.`,
  };
}

/** Строка заказа с характеристикой, помеченной на удаление в 1С, — для плашки. */
export interface DeletedCharacteristicLine {
  article: string;
  name: string;
  characteristic: string;
}

/** Строки заказа с характеристикой, помеченной на удаление (по одной на товар и
 *  характеристику: партии и единицы одной строки не дублируем). */
export function deletedCharacteristicLines(needs: NeedLine[]): DeletedCharacteristicLine[] {
  const seen = new Map<string, DeletedCharacteristicLine>();
  for (const n of needs) {
    if (!n.characteristic_deleted) continue;
    const key = `${n.nomenclature_ref}|${nrm(n.characteristic_ref)}`;
    if (!seen.has(key)) {
      seen.set(key, {
        article: n.nomenclature_article,
        name: n.nomenclature_name,
        characteristic: n.characteristic_name,
      });
    }
  }
  return [...seen.values()];
}

/** Коэффициент коробовой единицы, которую заказ требует для (ном, хар) (>1), иначе 0. */
export function requiredBoxCoef(needs: NeedLine[], nom: string, char: string): number {
  const c = nrm(char);
  const box = needs.find(
    (n) => n.nomenclature_ref === nom && (c ? nrm(n.characteristic_ref) === c : true) && n.unit_coef > 1,
  );
  return box ? box.unit_coef : 0;
}

// ─────────────────────── сборная коробка целиком ─────────────────────────

/** Строка сборной коробки — поля скана, по которым решает экран (`ResolvedScan.box_lines`). */
export type BoxLine = Pick<
  ResolvedScan,
  | "nomenclature_ref"
  | "nomenclature_name"
  | "nomenclature_article"
  | "characteristic_ref"
  | "characteristic_name"
  | "partia_ref"
  | "partia_name"
  | "unit_coefficient"
  | "qty_base"
  | "needs_characteristic"
  | "needs_partia"
>;

/** Почему строка коробки не принимается: заголовок и причина — те же, что у
 *  одиночного скана этой строки. */
export interface BoxRefusal {
  article: string;
  name: string;
  characteristic: string;
  partia: string;
  title: string;
  reason: string;
}

/** Правила раздела: сборка — товар не из заказа и перебор спрашивают человека;
 *  перемещения — перебор принимают, а не из заказа — только уже подтверждённую
 *  (ном, хар) из «нет в заказе» (`extras`). */
export type BoxMode =
  | { doc: "order" }
  | { doc: "transfer"; extras: Pick<ScannedExtra, "nomenclature_ref" | "characteristic_ref">[] };

/** Отказ: `refusals` — по строкам (пусто, если коробка уже в сессии), `text` —
 *  готовый текст героя. */
export type BoxDecision = { ok: true } | { ok: false; refusals: BoxRefusal[]; text: string };

/** Что уже в сессии: SSCC этой коробки (из ответа резолва) и позиции сессии
 *  (`ItemView.sscc` / `TransferItemView.sscc` — номер коробки, кодом которой
 *  записана позиция). */
export interface BoxInSession {
  sscc: string;
  items: ReadonlyArray<{ sscc?: string | null }>;
}

/** Массовое удаление сорвалось на середине: сколько строк сервер успел снять. */
export class BulkDeleteError extends Error {
  readonly removed: number;
  constructor(removed: number, cause: unknown) {
    super("Массовое удаление прервано", { cause });
    this.removed = removed;
  }
}

/**
 * Удалить выбранные строки ПО ОДНОЙ, по порядку.
 *
 * Сервер может снять больше, чем просили: строка сборной коробки уходит со
 * всей коробкой (одно место реестра, этап 3б). Что снято — знает только ответ
 * (`removed_item_ids`), поэтому следующую строку шлём, лишь если её ещё нет в
 * снятых; заранее «по одной на коробку» не угадываем. Первая ошибка обрывает
 * цикл — `BulkDeleteError` с числом уже снятых строк.
 *
 * @returns сколько строк сервер снял всего (вместе с соседями по коробке).
 */
export async function deleteItemsInOrder(
  ids: Iterable<string>,
  remove: (id: string) => Promise<{ removed_item_ids: string[] }>,
): Promise<number> {
  const removed = new Set<string>();
  for (const id of ids) {
    if (removed.has(id)) continue;
    try {
      const r = await remove(id);
      for (const x of r.removed_item_ids) removed.add(x);
    } catch (e) {
      throw new BulkDeleteError(removed.size, e);
    }
  }
  return removed.size;
}

/** Повтор коробки — тот же текст, что у одиночного отказа бэка по смыслу. */
export function boxAlreadyAcceptedText(sscc: string): string {
  return `Коробка ${sscc} уже принята в этой сессии`;
}

/**
 * Принять ли сборную коробку ЦЕЛИКОМ (решение 22.10.2026).
 *
 * Строка проходит, только если ЕЁ одиночный скан лёг бы в раздел сразу, без
 * вопроса и без черновика (`decideAfterResolve` страниц): товар есть в
 * документе, та характеристика, та партия, та единица, а у сборки ещё и нет
 * перебора. Иначе — отказ с той же причиной, что у одиночного скана. Перебор
 * считается НАКОПИТЕЛЬНО: собранное по строке документа + строки этой же
 * коробки, легшие в неё раньше. Не прошла хоть одна строка — не принимается
 * вся коробка: вопрос «добавить всё равно?» про одну строку из нескольких
 * задать нельзя, а записать часть коробки — недостача без объяснения.
 */
export function decideBox(
  needs: NeedLine[],
  lines: BoxLine[],
  mode: BoxMode,
  session: BoxInSession,
): BoxDecision {
  // ⚠️ Повтор — ДО построчных правил: принятая коробка уже лежит в «собрано»,
  // и построчно её второй скан выглядел бы «Перебором», а не тем, что он есть.
  if (session.sscc && session.items.some((i) => i.sscc === session.sscc)) {
    return { ok: false, refusals: [], text: boxAlreadyAcceptedText(session.sscc) };
  }
  const added = new Map<string, number>();
  const refusals: BoxRefusal[] = [];
  for (const line of lines) {
    const why = boxLineRefusal(needs, line, mode, added);
    if (why) {
      refusals.push({
        article: line.nomenclature_article,
        name: line.nomenclature_name,
        characteristic: line.characteristic_name,
        partia: line.partia_name,
        ...why,
      });
    }
  }
  return refusals.length ? { ok: false, refusals, text: boxRefusalText(refusals) } : { ok: true };
}

function boxLineRefusal(
  needs: NeedLine[],
  line: BoxLine,
  mode: BoxMode,
  added: Map<string, number>,
): { title: string; reason: string } | null {
  const nom = line.nomenclature_ref;
  const char = line.characteristic_ref;
  if (line.needs_characteristic && !nrm(char)) {
    return { title: "Нужна характеристика", reason: "Характеристика строки не определена — её спросил бы одиночный скан." };
  }
  if (line.needs_partia && !nrm(line.partia_ref)) {
    return { title: "Нужна партия", reason: "Партия строки не определена — её спросил бы одиночный скан." };
  }
  if (mode.doc === "order" && !needs.some((n) => n.nomenclature_ref === nom)) {
    return { title: "Нет в заказе", reason: "Этого товара нет в заказе." };
  }
  const coef = line.unit_coefficient || 1;
  const need = findNeed(needs, nom, char, line.partia_ref, coef);
  const boxCoef = requiredBoxCoef(needs, nom, char);
  if (!need && boxCoef > 1 && ck(coef) !== ck(boxCoef)) {
    return {
      title: `Нужны короба ×${fmtQty(boxCoef)}`,
      reason:
        coef > 1
          ? `В коробке ${fmtQty(coef)} шт — не тот размер упаковки.`
          : "В коробке 1 шт. Эта позиция — в коробах.",
    };
  }
  if (!need) {
    if (mode.doc === "transfer") {
      const confirmed = mode.extras.some(
        (e) => e.nomenclature_ref === nom && nrm(e.characteristic_ref) === nrm(char),
      );
      if (confirmed) return null;
    }
    const hero = notInOrderHero(
      needs, nom, { ref: char, name: line.characteristic_name }, line.partia_name, mode.doc,
    );
    return { title: hero.title, reason: hero.detail };
  }
  if (mode.doc === "transfer") return null; // сверх строки перемещения — принимаем
  const key = needGranKey(need);
  const before = added.get(key) ?? 0;
  added.set(key, before + line.qty_base);
  if (need.qty_scanned + before + line.qty_base > need.qty_needed + REMAINING_EPS) {
    const unit = need.base_unit_name ? ` ${need.base_unit_name}` : "";
    return {
      title: "Перебор",
      reason:
        `По заказу нужно ${fmtQty(need.qty_needed)}${unit}, собрано ${fmtQty(need.qty_scanned)}` +
        (before > 0 ? `, другие строки коробки — ${fmtQty(before)}` : "") +
        `; строка добавит ${fmtQty(line.qty_base)}.`,
    };
  }
  return null;
}

/** Тело записи строки коробки — общие поля `ItemCreate`/`TransferItemCreate`.
 *  Строка несёт код САМОЙ коробки (бэк по нему сверяет состав и повтор SSCC). */
export function boxLineBody(
  line: ResolvedScan,
  scan: { raw: string; ean13?: string; gtin?: string; serial?: string },
  scanKind: string,
) {
  return {
    scan_kind: scanKind,
    raw: scan.raw,
    ean13: scan.ean13 ?? null,
    gtin: scan.gtin ?? null,
    serial: scan.serial ?? null,
    nomenclature_ref: line.nomenclature_ref,
    nomenclature_name: line.nomenclature_name,
    nomenclature_article: line.nomenclature_article,
    characteristic_ref: line.characteristic_ref,
    characteristic_name: line.characteristic_name,
    partia_ref: line.partia_ref,
    partia_name: line.partia_name,
    partia_source: line.partia_ref ? line.partia_source || "manual" : "none",
    unit_ref: line.unit_ref,
    unit_coefficient: line.unit_coefficient,
    qty_scanned: line.qty_scanned,
    qty_base: line.qty_base,
  };
}

/** Отказы по строкам коробки — текстом для героя, строка на позицию. */
export function boxRefusalText(refusals: BoxRefusal[]): string {
  return refusals
    .map((r) => {
      const what = [r.article, r.name].filter(Boolean).join(" ");
      // Характеристика — строки коробки бывают одинаковы всем, кроме неё.
      const char = r.characteristic ? `, хар. ${r.characteristic}` : "";
      const partia = r.partia ? `, партия ${r.partia}` : "";
      return `• ${what}${char}${partia} — ${r.title}. ${r.reason}`;
    })
    .join("\n");
}

export type NeedStatus = "idle" | "partial" | "done" | "over";

/** Статус позиции заказа: не начато / недобор / точно собрано / перебор. */
export function needStatus(n: Pick<NeedLine, "qty_needed" | "qty_scanned">): NeedStatus {
  const s = n.qty_scanned;
  const need = n.qty_needed;
  if (s > need + REMAINING_EPS) return "over";
  if (Math.abs(s - need) <= REMAINING_EPS) return "done";
  if (s > REMAINING_EPS) return "partial";
  return "idle";
}

/** Проблемы наверх: перебор → недобор → не начато → готово. */
export const NEED_STATUS_ORDER: Record<NeedStatus, number> = {
  over: 0,
  partial: 1,
  idle: 2,
  done: 3,
};

/** Характеристика «ОПТ» — по имени, а не по ссылке: у склада их несколько
 * (общая и собственные у товаров), плюс помеченные на удаление «ОПТ- удаление»
 * (прод 20.10.2026). Признак — первое слово имени. */
export function isOptCharacteristic(name?: string | null): boolean {
  return (name ?? "").trim().split(/[\s-]+/)[0]?.toUpperCase() === "ОПТ";
}

type SheetSortBits = Pick<NeedLine, "characteristic_ref" | "characteristic_name" | "nomenclature_name" | "partia_name">;

/**
 * Порядок позиций в шторке «Позиции заказа» — как идут по складу: без
 * характеристики → с характеристикой (не ОПТ) → ОПТ в конце; внутри —
 * характеристика → наименование → партия (номер числом: «2» < «12»).
 * Статус на порядок НЕ влияет: фильтруют вкладки, а во «Все» строка не должна
 * прыгать после скана.
 */
export function compareNeedsForSheet(a: SheetSortBits, b: SheetSortBits): number {
  const group = (n: SheetSortBits) =>
    !nrm(n.characteristic_ref) ? 0 : isOptCharacteristic(n.characteristic_name) ? 2 : 1;
  const text = (x?: string | null, y?: string | null) =>
    (x ?? "").localeCompare(y ?? "", "ru", { numeric: true, sensitivity: "base" });
  return (
    group(a) - group(b) ||
    text(a.characteristic_name, b.characteristic_name) ||
    text(a.nomenclature_name, b.nomenclature_name) ||
    text(a.partia_name, b.partia_name)
  );
}

/**
 * Русское склонение по числу: `plural(1,["паллета","паллеты","паллет"])`.
 * Формы: [для 1, для 2–4, для 5+].
 */
export function plural(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const d = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (d > 1 && d < 5) return forms[1];
  if (d === 1) return forms[0];
  return forms[2];
}

/** «N паллет(а/ы)» готовой строкой. */
export function palletsWord(n: number): string {
  return `${n} ${plural(n, ["паллета", "паллеты", "паллет"])}`;
}
